import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  fetchPathLearnInit,
  markPathLearned,
  reportPathHintsShown
} from '../../api/fetchPath';
import { playUserAudio } from '../../utils/dialogUtils';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../context/AppConfigContext';
import styles from './PathLearn.module.css';

const HINTS_LIMIT = 3;
const HINT_AUTO_REVEAL_DELAY_MS = 10000;
const HINT_LANGUAGE_STORAGE_KEY_PREFIX = 'pathLearnHintLanguage';
const HINT_LANGUAGE_BASE = 'base';
const HINT_LANGUAGE_TARGET = 'target';

const normalizeLang = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return null;
  return normalized === 'ua' ? 'uk' : normalized;
};

const getHintLanguageStorageKey = (userId) =>
  `${HINT_LANGUAGE_STORAGE_KEY_PREFIX}:${String(userId || 'guest')}`;

const getTranslateLabelByLang = (lang) => {
  const normalized = normalizeLang(lang);
  if (normalized === 'ru') return 'Перевести';
  if (normalized === 'uk') return 'Перекласти';
  if (normalized === 'de') return 'Übersetzen';
  return 'Translate';
};

const PathLearn = ({ userId, step, onBack }) => {
  const { isAndroidApp } = useAuth();
  const { t, site, baseLanguage, targetLanguage, language } = useAppConfig();
  const pathText = t?.path || {};
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showWordTranslation, setShowWordTranslation] = useState(false);
  const [shownExampleTranslations, setShownExampleTranslations] = useState({});
  const [shownHintTranslations, setShownHintTranslations] = useState({});
  const [hintLanguageMode, setHintLanguageMode] = useState(null);
  const [hintRevealCount, setHintRevealCount] = useState(0);
  const [marking, setMarking] = useState(false);

  const autoRevealTimerRef = useRef(null);
  const trackedHintIdsRef = useRef(new Set());

  const resolveAudioSrc = (audioPath) => {
    if (!audioPath) return null;
    if (/^https?:\/\//i.test(audioPath)) return audioPath;
    return `${combinedIsAndroidApp ? site : ''}${audioPath}`;
  };

  const clearAutoRevealTimer = () => {
    if (autoRevealTimerRef.current) {
      clearTimeout(autoRevealTimerRef.current);
      autoRevealTimerRef.current = null;
    }
  };

  const currentItem = useMemo(() => items[currentIndex], [items, currentIndex]);
  const currentHints = useMemo(() => {
    const rawHints = Array.isArray(currentItem?.hints) ? currentItem.hints : [];
    return rawHints
      .filter((hint) => Number.isInteger(Number(hint?.id)) && String(hint?.text || '').trim())
      .slice(0, HINTS_LIMIT);
  }, [currentItem]);
  const visibleHints = useMemo(
    () => currentHints.slice(0, Math.min(hintRevealCount, currentHints.length)),
    [currentHints, hintRevealCount]
  );
  const hintButtonLabel = pathText.pathLearnHintsButton || 'Open grammar rules';
  const normalizedBaseLanguage = normalizeLang(baseLanguage);
  const normalizedTargetLanguage = normalizeLang(targetLanguage);
  const detectedHintLanguage = useMemo(() => {
    const firstHintTextLang = normalizeLang(currentHints[0]?.text_lang);
    return firstHintTextLang && firstHintTextLang === normalizedTargetLanguage
      ? HINT_LANGUAGE_TARGET
      : HINT_LANGUAGE_BASE;
  }, [currentHints, normalizedTargetLanguage]);
  const effectiveHintLanguage = hintLanguageMode || detectedHintLanguage;
  const canToggleHintLanguage =
    Boolean(normalizedBaseLanguage) &&
    Boolean(normalizedTargetLanguage) &&
    normalizedBaseLanguage !== normalizedTargetLanguage;
  const toggleToHintLanguage =
    effectiveHintLanguage === HINT_LANGUAGE_TARGET ? HINT_LANGUAGE_BASE : HINT_LANGUAGE_TARGET;
  const toggleHintLanguageLabel =
    toggleToHintLanguage === HINT_LANGUAGE_TARGET
      ? (pathText.pathLearnHintsSwitchToTarget || 'Show hints in target language')
      : (pathText.pathLearnHintsSwitchToBase || 'Show hints in base language');
  const hasSavedHintLanguageMode =
    hintLanguageMode === HINT_LANGUAGE_BASE || hintLanguageMode === HINT_LANGUAGE_TARGET;
  const showHintLanguageText = !hasSavedHintLanguageMode;
  const hintLanguageButtonText = pathText.pathLearnHintsTranslate || getTranslateLabelByLang(language);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const result = await fetchPathLearnInit({ userId, stepId: step?.id });
        if (active) {
          setItems(result?.words || []);
          setCurrentIndex(0);
        }
      } catch (error) {
        if (active) setItems([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    if (userId && step?.id) load();
    return () => {
      active = false;
    };
  }, [userId, step?.id]);

  useEffect(() => {
    if (!userId || typeof window === 'undefined') {
      setHintLanguageMode(null);
      return;
    }

    try {
      const savedMode = window.localStorage.getItem(getHintLanguageStorageKey(userId));
      if (savedMode === HINT_LANGUAGE_BASE || savedMode === HINT_LANGUAGE_TARGET) {
        setHintLanguageMode(savedMode);
        return;
      }
    } catch (_) {
      // Ignore localStorage errors and use server default behaviour.
    }

    setHintLanguageMode(null);
  }, [userId]);

  useEffect(() => {
    if (!userId || typeof window === 'undefined') return;

    const storageKey = getHintLanguageStorageKey(userId);
    try {
      if (hintLanguageMode === HINT_LANGUAGE_BASE || hintLanguageMode === HINT_LANGUAGE_TARGET) {
        window.localStorage.setItem(storageKey, hintLanguageMode);
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch (_) {
      // Ignore localStorage errors.
    }
  }, [hintLanguageMode, userId]);

  useEffect(() => {
    if (!currentItem?.word_audio) return;
    const audioSrc = resolveAudioSrc(currentItem.word_audio);
    playUserAudio(audioSrc);
  }, [currentItem?.word_audio, combinedIsAndroidApp]);

  useEffect(() => {
    setShowWordTranslation(false);
    setShownExampleTranslations({});
    setShownHintTranslations({});
    setHintRevealCount(0);
    trackedHintIdsRef.current = new Set();
    clearAutoRevealTimer();

    const autoOpenProbability = Math.max(
      0,
      Math.min(100, Number(currentItem?.hint_auto_open_probability) || 0)
    );

    if (!currentItem?.id || currentHints.length === 0 || autoOpenProbability <= 0) {
      return () => {
        clearAutoRevealTimer();
      };
    }

    autoRevealTimerRef.current = setTimeout(() => {
      if (Math.random() * 100 > autoOpenProbability) {
        return;
      }

      const firstHintId = Number(currentHints[0]?.id);
      setHintRevealCount(1);

      if (!Number.isInteger(firstHintId) || trackedHintIdsRef.current.has(firstHintId)) {
        return;
      }

      trackedHintIdsRef.current.add(firstHintId);
      void reportPathHintsShown({
        userId,
        wordId: currentItem.id,
        hintIds: [firstHintId]
      }).catch(() => {
        trackedHintIdsRef.current.delete(firstHintId);
      });
    }, HINT_AUTO_REVEAL_DELAY_MS);

    return () => {
      clearAutoRevealTimer();
    };
  }, [currentItem?.id, currentItem?.hint_auto_open_probability, currentHints, userId]);

  const handleNext = () => {
    clearAutoRevealTimer();
    if (currentIndex + 1 < items.length) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      onBack?.();
    }
  };

  const handleLearned = async () => {
    if (!currentItem) return;
    try {
      setMarking(true);
      await markPathLearned({ userId, stepId: step.id, wordId: currentItem.id });
      handleNext();
    } finally {
      setMarking(false);
    }
  };

  const toggleExampleTranslation = (exampleId, index) => {
    const key = String(exampleId ?? index);
    setShownExampleTranslations((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleHintTranslation = (hintId) => {
    const key = String(hintId);
    setShownHintTranslations((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleHintLanguage = () => {
    if (!canToggleHintLanguage) return;
    setShownHintTranslations({});
    setHintLanguageMode(toggleToHintLanguage);
  };

  const revealHints = (targetCount) => {
    if (!currentItem?.id || currentHints.length === 0) return;

    clearAutoRevealTimer();

    const nextCount = Math.max(0, Math.min(targetCount, currentHints.length, HINTS_LIMIT));
    if (nextCount <= hintRevealCount) return;

    const nextHintIds = currentHints
      .slice(0, nextCount)
      .map((hint) => Number(hint.id))
      .filter((hintId) => Number.isInteger(hintId) && !trackedHintIdsRef.current.has(hintId));

    setHintRevealCount(nextCount);

    if (!nextHintIds.length) return;

    nextHintIds.forEach((hintId) => trackedHintIdsRef.current.add(hintId));
    void reportPathHintsShown({
      userId,
      wordId: currentItem.id,
      hintIds: nextHintIds
    }).catch(() => {
      nextHintIds.forEach((hintId) => trackedHintIdsRef.current.delete(hintId));
    });
  };

  if (loading) {
    return <div className={styles.loading}>{pathText.pathLearn_loading || 'Loading...'}</div>;
  }

  if (!currentItem) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyTitle}>{pathText.pathLearn_empty || 'No words left'}</div>
        <button className={styles.primaryButton} onClick={onBack}>
          {pathText.pathLearn_back || 'Back'}
        </button>
      </div>
    );
  }

  const wordAudioSrc = resolveAudioSrc(currentItem.word_audio);
  const examples = Array.isArray(currentItem.examples) ? currentItem.examples : [];
  const displayWord =
    currentItem.article && currentItem.word ? `${currentItem.article} ${currentItem.word}` : currentItem.word;
  const canExpandHints = hintRevealCount > 0 && visibleHints.length < currentHints.length;
  const isHintsExpanded = visibleHints.length > 0;
  const resolveHintCopy = (hint) => {
    const primaryText = String(hint?.text || '').trim();
    const secondaryText = String(hint?.translation_text || '').trim();
    const primaryLang = normalizeLang(hint?.text_lang);
    const secondaryLang = normalizeLang(hint?.translation_lang);
    const preferredLang = effectiveHintLanguage === HINT_LANGUAGE_TARGET
      ? normalizedTargetLanguage
      : normalizedBaseLanguage;

    if (preferredLang && primaryLang === preferredLang) {
      return {
        text: primaryText,
        translation: secondaryText || null
      };
    }

    if (preferredLang && secondaryLang === preferredLang && secondaryText) {
      return {
        text: secondaryText,
        translation: primaryText || null
      };
    }

    return {
      text: primaryText,
      translation: secondaryText || null
    };
  };

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.backButton} onClick={onBack}>
          {pathText.pathLearn_back || 'Back'}
        </button>
        <div className={styles.progress}>
          {currentIndex + 1}/{items.length}
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.wordBlock}>
          <button
            className={styles.word}
            onClick={() => setShowWordTranslation((prev) => !prev)}
          >
            {displayWord}
          </button>
          {wordAudioSrc && (
            <button
              className={styles.audioButton}
              onClick={() => playUserAudio(wordAudioSrc)}
              aria-label={pathText.pathLearn_playWord || 'Play word'}
            >
              Play
            </button>
          )}
        </div>
        {showWordTranslation && (
          <div className={styles.translation}>{currentItem.translation}</div>
        )}
      </div>

      {examples.length > 0 && (
        <div className={styles.examplesWrap}>
          {examples.map((example, index) => {
            const exampleKey = String(example.id ?? index);
            const exampleAudioSrc = resolveAudioSrc(example.example_audio);
            return (
              <div className={styles.exampleCard} key={exampleKey}>
                <div className={styles.exampleBlock}>
                  <button
                    className={styles.example}
                    onClick={() => toggleExampleTranslation(example.id, index)}
                  >
                    {example.example}
                  </button>
                  {exampleAudioSrc && (
                    <button
                      className={styles.audioButton}
                      onClick={() => playUserAudio(exampleAudioSrc)}
                      aria-label={pathText.pathLearn_playExample || 'Play example'}
                    >
                      Play
                    </button>
                  )}
                </div>
                {shownExampleTranslations[exampleKey] && (
                  <div className={styles.translation}>{example.example_translation}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {currentHints.length > 0 && (
        <div className={`${styles.hintsDock} ${isHintsExpanded ? styles.hintsDockExpanded : ''}`}>
          <button
            type="button"
            className={`${styles.hintBookButton} ${hintRevealCount > 0 ? styles.hintBookButtonActive : ''}`}
            onClick={() => revealHints(1)}
            aria-label={hintButtonLabel}
            title={hintButtonLabel}
          >
            <svg viewBox="0 0 24 24" className={styles.hintBookIcon} aria-hidden="true">
              <path
                d="M4 5.5C4 4.12 5.12 3 6.5 3H20v15.5A2.5 2.5 0 0 0 17.5 16H6.5A2.5 2.5 0 0 1 4 13.5v-8Z"
                fill="currentColor"
                opacity="0.15"
              />
              <path
                d="M6.5 4h12.75v12.5H6.5A3.5 3.5 0 0 0 3 20h13.5a2.5 2.5 0 0 1 2.5 2.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.75 7.5h8M7.75 10.5h6M7.75 13.5h5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {visibleHints.length > 0 && (
            <div className={styles.hintPanel}>
              <div className={styles.hintPanelHeader}>
                <span className={styles.hintPanelTitle}>
                  {pathText.pathLearnHintsTitle || 'Grammar hint'}
                </span>
                <div className={styles.hintPanelHeaderControls}>
                  {canToggleHintLanguage && (
                    <button
                      type="button"
                      className={`${styles.hintLanguageButton} ${!showHintLanguageText ? styles.hintLanguageButtonIconOnly : ''}`}
                      onClick={toggleHintLanguage}
                      aria-label={toggleHintLanguageLabel}
                      title={toggleHintLanguageLabel}
                    >
                      <span className={styles.hintLanguageIcon} aria-hidden="true">🌐</span>
                      {showHintLanguageText && (
                        <span className={styles.hintLanguageText}>{hintLanguageButtonText}</span>
                      )}
                    </button>
                  )}
                  {canExpandHints && (
                    <button
                      type="button"
                      className={styles.hintMoreButton}
                      onClick={() => revealHints(HINTS_LIMIT)}
                    >
                      {pathText.pathLearnHintsMore || 'More'}
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.hintList}>
                {visibleHints.map((hint) => {
                  const exampleNumbers = Array.isArray(hint.example_numbers)
                    ? hint.example_numbers
                        .map((value) => Number(value))
                        .filter((value) => Number.isInteger(value) && value > 0)
                    : [];
                  const hintKey = String(hint.id);
                  const hintCopy = resolveHintCopy(hint);
                  const isHintTranslationVisible = Boolean(shownHintTranslations[hintKey]);
                  const canToggleHintTranslation = Boolean(hintCopy.translation);

                  return (
                    <div className={styles.hintCard} key={hint.id}>
                      <button
                        type="button"
                        className={`${styles.hintCardButton} ${canToggleHintTranslation ? styles.hintCardButtonInteractive : ''}`}
                        onClick={() => canToggleHintTranslation && toggleHintTranslation(hint.id)}
                        disabled={!canToggleHintTranslation}
                      >
                        <span className={styles.hintCardText}>{hintCopy.text}</span>
                        {isHintTranslationVisible && hintCopy.translation && (
                          <span className={styles.hintCardTranslation}>{hintCopy.translation}</span>
                        )}
                      </button>
                      {exampleNumbers.length > 0 && (
                        <div className={styles.hintExampleBadges} aria-label="Related example numbers">
                          {exampleNumbers.map((value) => (
                            <span className={styles.hintExampleBadge} key={`${hint.id}-${value}`}>
                              {value}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={styles.secondaryButton}
          onClick={handleLearned}
          disabled={marking}
        >
          {pathText.pathLearn_markLearned || 'Learned'}
        </button>
        <button className={styles.primaryButton} onClick={handleNext}>
          {pathText.pathLearn_next || 'Next'}
        </button>
      </div>
    </div>
  );
};

export default PathLearn;
